// frontend/src/components/government/ReimbursementSubmissionModal.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardList,
  User,
  Baby,
  CalendarDays,
  Clock,
  Wallet,
  Copy,
  Info,
  AlertTriangle,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import Modal from '../UI/Modal';
import { LeaveTypeBadge } from '../UI/LeaveTypeBadge';
import { StatusBadge } from '../UI/StatusBadge';
import LeavePeriodDisplay from '../Leave/LeavePeriodDisplay';
import { formatDate } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { copyToClipboard } from '../../utils/clipboard';

const formatSGD = (value) =>
  value == null
    ? null
    : new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value);

const ordinal = (n) => {
  const num = parseInt(n, 10);
  if (!Number.isFinite(num)) return n;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return `${num}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const titleCase = (value) =>
  typeof value === 'string'
    ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : value;

// Collapse a list of working day names into compact ranges,
// e.g. ['Monday',...,'Friday'] -> 'Monday to Friday'.
const WEEKDAY_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const formatWorkingDays = (days) => {
  if (!Array.isArray(days) || !days.length) return null;
  const idx = days
    .map((d) => WEEKDAY_ORDER.indexOf(d))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
  if (!idx.length) return days.join(', ');
  const ranges = [];
  let start = idx[0];
  let prev = idx[0];
  for (let i = 1; i < idx.length; i += 1) {
    if (idx[i] === prev + 1) {
      prev = idx[i];
    } else {
      ranges.push([start, prev]);
      start = idx[i];
      prev = idx[i];
    }
  }
  ranges.push([start, prev]);
  return ranges
    .map(([s, e]) => (s === e ? WEEKDAY_ORDER[s] : `${WEEKDAY_ORDER[s]} to ${WEEKDAY_ORDER[e]}`))
    .join(', ');
};

// claim_status -> StatusBadge status + label (mirrors the tracker list)
const STATUS_META = {
  to_claim: { badge: 'pending', label: 'To Claim' },
  pending: { badge: 'pending', label: 'Draft' },
  submitted: { badge: 'submitted', label: 'Submitted' },
  approved: { badge: 'approved', label: 'Govt Approved' },
  paid: { badge: 'completed', label: 'Reimbursed' },
  rejected: { badge: 'rejected', label: 'Rejected' },
};

const handleCopy = async (value, label) => {
  const ok = await copyToClipboard(value);
  if (ok) toast.success(`${label} copied`);
  else toast.error('Copy failed');
};

// One label/value row. `copyable` adds a copy-to-clipboard button (handy for the
// fields HR transcribes into the MSF portal: NRIC/FIN, salary, dates).
const Field = ({ label, value, copyable = false, copyValue, mono = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 flex-shrink-0 w-40 pt-0.5">{label}</span>
      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
        <span
          className={`text-sm text-right break-words ${
            isEmpty ? 'text-gray-400 italic' : 'text-gray-900'
          } ${mono && !isEmpty ? 'font-mono tracking-tight' : ''}`}
        >
          {isEmpty ? 'Not on file' : value}
        </span>
        {copyable && !isEmpty && (
          <button
            type="button"
            onClick={() => handleCopy(copyValue ?? value, label)}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
            title={`Copy ${label}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden">
    <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
      <Icon className="w-4 h-4 text-red-600" />
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
    </div>
    <div className="px-4 py-1">{children}</div>
  </div>
);

const buildCopyAllText = (packet) => {
  const { claim, employee, child, leave, work_pattern: work, salary } = packet;
  const d = (v) => (v ? formatDate(v) : '-');
  const lines = [
    'MSF Government-Paid Leave — Submission Details',
    '',
    '[Claim]',
    `Scheme: ${claim.scheme_label || '-'}`,
    `Claim period: ${d(claim.claim_period_start)} to ${d(claim.claim_period_end)}`,
    `Status: ${STATUS_META[claim.claim_status]?.label || claim.claim_status || '-'}`,
    `Submission deadline: ${d(claim.submission_deadline)}`,
    '',
    '[Employee]',
    `Name: ${employee.full_name || '-'}`,
    `${employee.id_type || 'ID'}: ${employee.nric_fin || '-'}`,
    `Date of birth: ${d(employee.date_of_birth)}`,
    `Employment start: ${d(employee.employment_start_date)}`,
    `Employment type: ${titleCase(employee.employment_type) || '-'}`,
    `Employer entity: ${employee.employer_entity || '-'}`,
    '',
    '[Child]',
    `Name: ${child.full_name || '-'}`,
    `${child.date_is_estimated ? 'Est. delivery date' : 'Date of birth'}: ${d(child.date_of_birth)}`,
    `Birth order: ${child.birth_order ? ordinal(child.birth_order) : '-'}`,
    `Citizenship: ${titleCase(child.citizenship_status) || '-'}`,
    ...(child.is_adoption
      ? [`Formal intent to adopt: ${d(child.formal_intent_to_adopt_date)}`]
      : []),
    '',
    '[Leave]',
    `Period: ${d(leave.start_date)} to ${d(leave.end_date)}`,
    `Total days: ${formatLeaveDays(leave.total_days)}`,
    `Claimable working days: ${formatLeaveDays(leave.reimbursable_units)} ${leave.unit_label || ''}`.trim(),
    '',
    '[Work pattern]',
    `Working days/week: ${formatLeaveDays(work.working_days_per_week)}`,
    `Schedule: ${work.schedule_name || '-'}`,
    '',
    '[Salary]',
    `Monthly basic: ${formatSGD(salary.monthly_basic_salary) || '-'}`,
    `Monthly allowances: ${formatSGD(salary.monthly_allowances) || '-'}`,
    `Monthly gross: ${formatSGD(salary.monthly_gross) || '-'}`,
    `Weekly equivalent: ${formatSGD(salary.weekly_salary_equivalent) || '-'}`,
    `Employer CPF (est.): ${formatSGD(salary.employer_cpf_estimated) || '-'}`,
  ];
  return lines.join('\n');
};

const ReimbursementSubmissionModal = ({ leaveApplicationId, onClose }) => {
  const isOpen = !!leaveApplicationId;

  const { data: packet, isLoading, isError } = useQuery({
    queryKey: ['reimbursement-submission', leaveApplicationId],
    queryFn: async () => {
      const res = await staffAPI.getReimbursementSubmissionDetail(leaveApplicationId);
      return res.data;
    },
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const claim = packet?.claim || {};
  const employee = packet?.employee || {};
  const child = packet?.child || {};
  const leave = packet?.leave || {};
  const work = packet?.work_pattern || {};
  const salary = packet?.salary || {};

  const workingDaysSummary = formatWorkingDays(work.working_days);

  // Child particulars are only captured for child-linked schemes (maternity /
  // paternity / adoption). Childcare leave (CCL/ECCL) is claimed at the
  // employee level, so the child section is often legitimately empty.
  const hasChildData = Boolean(
    child.full_name || child.date_of_birth || child.birth_order || child.citizenship_status || child.nric_fin,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="GPL Submission Details"
      size="lg"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Close
          </button>
          {packet && (
            <button
              type="button"
              onClick={() => handleCopy(buildCopyAllText(packet), 'Submission details')}
              className="btn btn-admin-primary"
            >
              <Copy className="w-4 h-4" />
              Copy all
            </button>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="py-10">
          <LoadingSpinner />
        </div>
      ) : isError || !packet ? (
        <div className="py-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600">
            Could not load submission details for this leave application.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Context banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900">
              The MSF Government-Paid Leave portal isn&apos;t connected to this HR portal.
              Use the details below to lodge the claim manually — tap the copy icon on any
              field, or <strong>Copy all</strong> for the full packet.
            </p>
          </div>

          {/* Header summary */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{employee.full_name}</span>
            <span className="text-sm text-gray-500">({employee.staff_id})</span>
            <span className="text-gray-300">·</span>
            <LeaveTypeBadge leaveTypeName={leave.leave_type_name} size="xs" />
            <StatusBadge
              status={STATUS_META[claim.claim_status]?.badge || 'pending'}
              label={STATUS_META[claim.claim_status]?.label}
            />
          </div>

          <Section icon={ClipboardList} title="Claim">
            <Field label="Scheme" value={claim.scheme_label} />
            <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-50">
              <span className="text-xs text-gray-500 flex-shrink-0 w-40 pt-0.5">Claim period</span>
              <div className="min-w-0 flex-1 text-right text-sm text-gray-900">
                <LeavePeriodDisplay
                  application={{
                    leave_start_date: claim.claim_period_start,
                    leave_end_date: claim.claim_period_end,
                    leave_start_time_period: leave.start_time_period,
                    leave_end_time_period: leave.end_time_period,
                  }}
                  totalDays={leave.total_days}
                  showIcon={false}
                />
              </div>
            </div>
            <Field label="Status" value={STATUS_META[claim.claim_status]?.label || titleCase(claim.claim_status)} />
            <Field label="Claim reference" value={claim.claim_reference_number} copyable />
            <Field
              label="Submitted on"
              value={claim.submitted_date ? formatDate(claim.submitted_date) : null}
            />
            <Field
              label="Submission deadline"
              value={
                claim.submission_deadline ? (
                  <span className={claim.is_overdue ? 'text-red-600 font-medium' : ''}>
                    {formatDate(claim.submission_deadline)}
                    {claim.is_overdue && ' (overdue)'}
                    {!claim.is_overdue && typeof claim.days_until_deadline === 'number' && (
                      <span className="block text-xs text-gray-400">
                        {claim.days_until_deadline} day(s) left
                      </span>
                    )}
                  </span>
                ) : null
              }
            />
          </Section>

          <Section icon={User} title="Employee Details">
            <Field label="Full name" value={employee.full_name} copyable />
            <Field
              label={employee.id_type || 'NRIC / FIN'}
              value={employee.nric_fin}
              copyable
              mono
            />
            <Field
              label="Date of birth"
              value={employee.date_of_birth ? formatDate(employee.date_of_birth) : null}
            />
            <Field
              label="Employment start"
              value={
                employee.employment_start_date ? formatDate(employee.employment_start_date) : null
              }
            />
            <Field label="Length of service" value={employee.length_of_service} />
            <Field label="Employment type" value={titleCase(employee.employment_type)} />
            <Field label="Designation" value={employee.designation} />
            <Field label="Department" value={employee.department} />
            <Field label="Employer entity" value={employee.employer_entity} copyable />
            <Field
              label="CPF Submission No. (CSN)"
              value={employee.cpf_submission_number}
            />
          </Section>

          <Section icon={Baby} title="Child's Particulars">
            {!hasChildData ? (
              <p className="text-xs text-gray-500 py-3">
                No child linked to this leave application. Childcare leave (CCL / ECCL) is
                claimed at the employee level, so a child record isn&apos;t captured here —
                add the child&apos;s details directly in the MSF GPL portal if the scheme
                requires it.
              </p>
            ) : (
            <>
            <Field label="Child name" value={child.full_name} copyable />
            <Field
              label={child.date_is_estimated ? 'Est. delivery date (EDD)' : 'Date of birth'}
              value={child.date_of_birth ? formatDate(child.date_of_birth) : null}
              copyable
              copyValue={child.date_of_birth ? formatDate(child.date_of_birth) : ''}
            />
            <Field label="Child order" value={child.birth_order ? ordinal(child.birth_order) : null} />
            <Field label="Citizenship" value={titleCase(child.citizenship_status)} />
            {child.nric_fin && <Field label="Child NRIC/FIN" value={child.nric_fin} copyable mono />}
            {child.is_adoption && (
              <>
                <Field
                  label="Formal intent to adopt"
                  value={
                    child.formal_intent_to_adopt_date
                      ? formatDate(child.formal_intent_to_adopt_date)
                      : null
                  }
                />
                <Field label="Adoption cert no." value={child.adoption_certificate_number} />
              </>
            )}
            </>
            )}
          </Section>

          <Section icon={CalendarDays} title="Leave Details">
            <Field
              label="Leave start date"
              value={leave.start_date ? formatDate(leave.start_date) : null}
            />
            <Field
              label="Leave end date"
              value={leave.end_date ? formatDate(leave.end_date) : null}
            />
            <Field
              label="Total days"
              value={`${formatLeaveDays(leave.total_days)} ${pluralizeDays(leave.total_days)}`}
            />
            <Field
              label="Claimable working days"
              value={
                leave.reimbursable_units
                  ? `${formatLeaveDays(leave.reimbursable_units)} ${leave.unit_label || ''}`.trim()
                  : null
              }
            />
            {leave.coverage_position && <Field label="Coverage" value={leave.coverage_position} />}
          </Section>

          <Section icon={Clock} title="Work Pattern">
            <Field
              label="Working days/week"
              value={work.working_days_per_week ? formatLeaveDays(work.working_days_per_week) : null}
            />
            <Field label="Work schedule" value={work.schedule_name || work.schedule_type || null} />
            <Field label="Working days" value={workingDaysSummary} />
          </Section>

          <Section icon={Wallet} title="Salary Details">
            <Field
              label="Monthly basic"
              value={formatSGD(salary.monthly_basic_salary)}
              copyable
              copyValue={salary.monthly_basic_salary}
              mono
            />
            <Field
              label="Monthly allowances"
              value={formatSGD(salary.monthly_allowances)}
              mono
            />
            <Field
              label="Monthly gross"
              value={formatSGD(salary.monthly_gross)}
              copyable
              copyValue={salary.monthly_gross}
              mono
            />
            <Field
              label="Weekly equivalent"
              value={formatSGD(salary.weekly_salary_equivalent)}
              mono
            />
            <Field
              label="Employer CPF (est.)"
              value={
                salary.employer_cpf_estimated != null
                  ? `${formatSGD(salary.employer_cpf_estimated)}${
                      salary.employer_cpf_rate != null ? ` (@ ${salary.employer_cpf_rate}%)` : ''
                    }`
                  : null
              }
              mono
            />
            <Field label="Pay type" value={salary.pay_type} />
            <Field
              label="Reimbursement cap"
              value={
                salary.rate_cap
                  ? `${formatSGD(salary.rate_cap)} / ${leave.unit_label || 'unit'}`
                  : null
              }
            />
            <Field
              label="Est. reimbursement"
              value={formatSGD(salary.estimated_amount)}
              mono
            />
          </Section>

          <p className="text-[11px] text-gray-400 px-1">
            Identity, salary &amp; CPF data are read from confidential records (HR-admin only).
            Monthly gross = monthly basic + contractual monthly allowances. Employer CPF is an
            estimate on basic pay; estimated reimbursement is an upper-bound at the statutory
            cap (MSF confirms the exact amount from CPF wage data).
          </p>
        </div>
      )}
    </Modal>
  );
};

export default ReimbursementSubmissionModal;
